import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {useEffect, useMemo, useRef} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Sync} from '@client/sync'
import {ActionsPanel} from '@components/common/base/buttons/actions-panel/actions-panel'
import {Input} from '@components/common/base/input'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {useGlobalKeyDown} from '@containers/hooks'
import {Destination} from './destination'
import {getTemplate} from './helpers/get-template'
import {LinkButtonWrapped} from './link-button-wrapped'
import {useMovementStore} from './movement-store'
import {PinStickersActions} from './pin-stickers-actions'
import {RemoveAssignedActions} from './remove-assigned-actions'
import {SelectedColumns} from './selected-columns'
import {SubscribersActions} from './subscribers-actions'
import {UnpinStickersActions} from './unpin-stickers-actions'

const TwMainContainer = tw.div`
  flex-1
  flex
  flex-col
  h-full
  relative
  pb-[80px]
  min-h-0
`

const TwTitle = tw.div`
  flex
  items-center
  text-primary
  text-sm-semibold
  h-32
  cursor-default
  shrink-0
`

const TwContentContainer = tw.div`
  mt-8
  ml-8
  shrink-0
`

const TwText = tw.div`
  text-primary
  plain-text-regular
  cursor-default
`

export const MovementTemplatesEdit = observer(() => {
  const [state, store] = useMovementStore()
  const inputRef = useRef<HTMLInputElement>()

  const board = useMemo(() => {
    return Sync.getCurrentSandbox().getHub(store.boardId)
  }, [store.boardId])

  const destBoard = useMemo(() => {
    return Sync.getCurrentSandbox().getHub(state.destination?.board?.id)
  }, [state.destination?.board?.id])

  const editId = state.editId
  const editHub = useMemo(() => getTemplate(editId), [editId])

  useEffect(() => {
    store.initState(editHub)
  }, [editId])

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus()
    }, 10)
  }, [state.editId])

  const save = () => {
    store.save(editHub)
    return true
  }

  useGlobalKeyDown({
    escape: store.cancel,
    enter: () => save()
  })

  useEffect(() => {
    const listener = (ev: KeyboardEvent) => {
      const key = ev.key.toLowerCase()
      ev.stopPropagation()

      if (key === 'enter') {
        save()
      }
      if (key === 'escape') {
        store.cancel()
      }
    }

    inputRef.current?.addEventListener('keydown', listener)

    return () => {
      inputRef.current?.removeEventListener('keydown', listener)
    }
  }, [])

  const canBeSubmitted = store.canSubmit(editHub)
  const {title, destination, columnsFrom, unpinStickers, pinStickers} = state
  const lang = Lang.msg.movementTemplates

  return (
    <TwMainContainer>
      <LinkButtonWrapped
        icon={'IconCustomArrowLeft'}
        onMouseDown={store.goToMain}
        text={lang.backToList}
        height={'h-32'}
        className={'mb-8 shrink-0'}
      />

      <TwTitle>
        {!editHub
          ? lang.createTitle
          : lang.editTitle(editHub.data.movementTemplate?.title || '')}
      </TwTitle>

      <WithPerfectScrollbar className="min-h-40 pb-60 pr-16 -mr-16">
        <TwContentContainer>
          <Input
            ref={inputRef}
            value={title}
            onChange={({text}) => store.setTitle(text)}
            placeholder={lang.createTitlePlaceholder}
            size="32"
            className="!w-320"
          />

          <TwText className="mb-8 mt-16">{lang.createMoveTo}</TwText>
          <Destination value={destination} onChange={store.setDestination} />

          <TwText className="mb-8 mt-16">{lang.createAvailableFrom}</TwText>
          <SelectedColumns
            columns={columnsFrom}
            onChange={store.setColumnsFrom}
          />

          <TwText className="mb-4 mt-20">{lang.createActionsAfter}</TwText>
          {board && (
            <>
              <RemoveAssignedActions board={board} destBoard={destBoard} />
              <SubscribersActions board={board} destBoard={destBoard} />
            </>
          )}
          <UnpinStickersActions
            stickersOptions={unpinStickers}
            onChange={store.unpinStickersChanged}
          />
          {board && (
            <PinStickersActions board={destBoard} values={pinStickers} />
          )}
        </TwContentContainer>
      </WithPerfectScrollbar>

      <ActionsPanel
        className="!bottom-8"
        show={canBeSubmitted}
        primaryClick={save}
        primaryText={editHub ? lang.saveButton : lang.createButton}
        secondaryText={Lang.msg.cancel}
        secondaryClick={store.cancelForce}
        error={state.errorHasChanges}
        errorText={Lang.msg.errorNotSaveData}
        errorBg={state.errorHasChangesBgRed}
      />
    </TwMainContainer>
  )
})
